import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api, { errMsg } from '../../lib/api';
import { AlertTriangle, Check } from 'lucide-react';

export const StageStepper = ({ stages, current }) => {
  const idx = stages.indexOf(current);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="ludo-stage-stepper">
      {stages.map((s, i) => (
        <div key={s} className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border ${
          i < idx ? 'bg-[#EAF1EE] border-[#C9DAD2] text-[#4C6F62]' : i === idx ? 'bg-[#5E5068] border-[#5E5068] text-white' : 'bg-white border-[#E5E0EA] text-[#8A7B95]'}`}>
          {i < idx && <Check size={11} />}{s}
        </div>
      ))}
    </div>
  );
};

export const StageMover = ({ client, schema, onMoved }) => {
  const [target, setTarget] = useState(client.next_stage || '');
  const [note, setNote] = useState('');
  const [missing, setMissing] = useState(null);
  const [override, setOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const all = [...schema.stages, ...schema.side_stages].filter((s) => s !== client.stage);

  const move = async (withOverride) => {
    setBusy(true);
    try {
      const r = await api.post(`/ludo/clients/${client.client_id}/stage`, {
        stage: target, note, override_reason: withOverride ? override : null,
      });
      toast.success(`Moved to ${target}`);
      setMissing(null); setNote(''); setOverride('');
      onMoved(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.missing) setMissing(d.missing);
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0EA] bg-[#FAF9FC] p-4 space-y-3" data-testid="ludo-stage-mover">
      <div className="flex gap-2 flex-wrap items-center">
        <select value={target} onChange={(e) => { setTarget(e.target.value); setMissing(null); }}
          className="h-9 rounded-xl border border-[#E5E0EA] bg-white px-3 text-sm" data-testid="ludo-stage-select">
          <option value="">Move to…</option>
          {all.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={schema.side_stages.includes(target) ? 'Reason (required)' : 'Note (optional)'}
          className="rounded-xl h-9 flex-1 min-w-[160px]" data-testid="ludo-stage-note" />
        <Button disabled={!target || busy} onClick={() => move(false)} className="rounded-xl bg-[#5E5068] hover:bg-[#4A3F53] text-white" data-testid="ludo-stage-move-btn">Move</Button>
      </div>
      {missing && (
        <div className="rounded-xl border border-[#E8C9C9] bg-[#FBEDED] p-3 text-[13px]" data-testid="ludo-stage-missing">
          <div className="font-semibold text-[#8A3A3A] flex items-center gap-1.5"><AlertTriangle size={14} /> Can't move to {target} yet — missing:</div>
          <ul className="list-disc ml-5 mt-1 text-[#5A3A3A]">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
          <div className="flex gap-2 mt-3">
            <Input value={override} onChange={(e) => setOverride(e.target.value)} placeholder="Override reason (logged)" className="rounded-xl h-9 bg-white" data-testid="ludo-stage-override-reason" />
            <Button variant="outline" disabled={!override.trim() || busy} onClick={() => move(true)} className="rounded-xl" data-testid="ludo-stage-override-btn">Move anyway</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageMover;
